import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import BtnTaratic from '../../components/ui/BtnTaratic';
import { clinicSchedulesService, type ClinicHoliday } from '../../services/clinicSchedulesService';
import { useNotifications } from '../../components/ui/notifications';

interface ClinicsHolidayModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  holidayToEdit?: ClinicHoliday | null;
}

const ClinicsHolidayModal: React.FC<ClinicsHolidayModalProps> = ({
  isOpen,
  onClose,
  onSave,
  holidayToEdit
}) => {
  const notifications = useNotifications();
  const [loading, setLoading] = useState(false);

  const [fecha, setFecha] = useState('');
  const [estaAbierto, setEstaAbierto] = useState(false);
  const [horaAperturaEspecial, setHoraAperturaEspecial] = useState('');
  const [horaCierreEspecial, setHoraCierreEspecial] = useState('');
  const [descripcion, setDescripcion] = useState('');

  useEffect(() => {
    if (isOpen && holidayToEdit) {
      setFecha(holidayToEdit.fecha);
      setEstaAbierto(holidayToEdit.esta_abierto);
      setHoraAperturaEspecial(holidayToEdit.hora_apertura_especial || '');
      setHoraCierreEspecial(holidayToEdit.hora_cierre_especial || '');
      setDescripcion(holidayToEdit.descripcion || '');
    } else if (isOpen) {
      setFecha('');
      setEstaAbierto(false);
      setHoraAperturaEspecial('');
      setHoraCierreEspecial('');
      setDescripcion('');
    }
  }, [isOpen, holidayToEdit]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        fecha,
        esta_abierto: estaAbierto,
        hora_apertura_especial: estaAbierto ? (horaAperturaEspecial || null) : null,
        hora_cierre_especial: estaAbierto ? (horaCierreEspecial || null) : null,
        descripcion: descripcion || null,
      };

      if (holidayToEdit) {
        await clinicSchedulesService.updateHoliday(holidayToEdit.id, data);
      } else {
        await clinicSchedulesService.createHoliday(data);
      }
      notifications.success(
        holidayToEdit ? 'Día especial actualizado' : 'Día especial creado',
        'La configuración se guardó correctamente.',
      );
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving holiday', err);
      notifications.error('No se pudo guardar el día especial', 'Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-ui="clinics-holiday-modal-region-001" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div data-ui="clinics-holiday-modal-region-002" className="bg-[#0f172a] rounded-3xl w-full max-w-lg overflow-hidden border border-white/10 shadow-2xl flex flex-col max-h-[90vh]">
        <div data-ui="clinics-holiday-modal-region-003" className="flex justify-between items-center p-6 border-b border-white/10 bg-white/5">
          <h2 className="text-xl font-bold text-white">
            {holidayToEdit ? 'Editar Día Especial' : 'Añadir Día Especial'}
          </h2>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <div data-ui="clinics-holiday-modal-region-004" className="p-6 overflow-y-auto">
          <form id="holiday-form" onSubmit={handleSubmit} className="space-y-4">
            
            <div data-ui="clinics-holiday-modal-region-005">
              <label className="block text-sm font-medium text-white/80 mb-1">Fecha</label>
              <input
                type="date"
                required
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
              />
            </div>

            <div data-ui="clinics-holiday-modal-region-006">
              <label className="block text-sm font-medium text-white/80 mb-1">Descripción</label>
              <input
                type="text"
                placeholder="Ej. Festivo Nacional, Navidad..."
                required
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
              />
            </div>

            <div data-ui="clinics-holiday-modal-region-007" className="flex items-center gap-3 pt-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={estaAbierto}
                  onChange={(e) => setEstaAbierto(e.target.checked)}
                />
                <div data-ui="clinics-holiday-modal-region-008" className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-white/80">¿Estará abierta la clínica?</span>
              </label>
            </div>

            {estaAbierto && (
              <div data-ui="clinics-holiday-modal-region-009" className="grid grid-cols-2 gap-4 pt-2 border-t border-white/10 mt-4">
                <div data-ui="clinics-holiday-modal-region-010">
                  <label className="block text-sm font-medium text-white/80 mb-1">Hora de Apertura</label>
                  <input
                    type="time"
                    required={estaAbierto}
                    value={horaAperturaEspecial}
                    onChange={(e) => setHoraAperturaEspecial(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                  />
                </div>

                <div data-ui="clinics-holiday-modal-region-011">
                  <label className="block text-sm font-medium text-white/80 mb-1">Hora de Cierre</label>
                  <input
                    type="time"
                    required={estaAbierto}
                    value={horaCierreEspecial}
                    onChange={(e) => setHoraCierreEspecial(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                  />
                </div>
              </div>
            )}

          </form>
        </div>

        <div data-ui="clinics-holiday-modal-region-012" className="p-6 border-t border-white/10 bg-white/5 flex justify-end gap-3">
          <BtnTaratic color="ghost-dark" onClick={onClose} disabled={loading}>
            Cancelar
          </BtnTaratic>
          <BtnTaratic color="green" type="submit" form="holiday-form" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Día Especial'}
          </BtnTaratic>
        </div>
      </div>
    </div>
  );
};

export default ClinicsHolidayModal;
